import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { observer } from 'mobx-react';
import {
  IconLibrary,
  Layout,
  StackablePageButtons,
  FormFields,
  PageSection,
  PermissionButton,
} from '@product-framework/index';
import {
  Breadcrumb,
  CRUD_COMPONENT_TYPE,
  CRUDComponent,
  useDataProvider,
  useLocalization,
  useSnackbar,
  useStore,
} from '@ui-framework/index';

import { useNavigate } from 'react-router-dom';
import { createSchema } from '../../schemas';
import validationSchema from './validations';

function ServiceAdministratorWarrantyTypeCreate() {
  const { formatMessage } = useLocalization();
  const createFormRef = useRef();
  const navigate = useNavigate();
  const snackbar = useSnackbar();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { post, get, getApiUrl } = useDataProvider(provider);
  const [formErrors, setFormErrors] = useState(null);
  const store = useStore();
  const storeName = 'serviceadministratorwarrantytypeFormStore';

  const postApiUrl = `${getApiUrl()}/sxcl/v1/service-administrator-warranty-types`;

  const preProcessRequestData = data => {
    const { status, createdByUserId, lastModifiedByUserId, ...other } = data;
    return {
      status: status === 'true',
      ...other,
    };
  };

  const submitCreateForm = async data => {
    setFormErrors(null);
    const requestData = preProcessRequestData(data);
    store.uiState[storeName].set(requestData);
    const response = await post(postApiUrl, { ...requestData });
    if (response?.ok) {
      store.uiState[storeName].set({});
      snackbar.showMessage(
        formatMessage({
          id: 'SERVICE_ADMINISTRATOR_WARRANTY_TYPES_WAS_SAVED_SUCCESSFULLY',
          defaultMessage:
            'Service Administrator Warranty Types was saved successfully',
        }),
      );
      createFormRef.current?.resetAndNavigate(
        `/serviceadministratorwarrantytypes/edit/${response?.data?.id}`,
      );
    } else {
      setFormErrors(response.errors);
      snackbar.showMessage({
        type: 'error',
        data: formatMessage({
          id: 'SERVICEADMINISTRATORWARRANTYTYPE_CREATE_FAILURE',
          defaultMessage:
            'Error: Could not save the service administrator warranty types',
        }),
      });
    }
  };

  const formProps = {
    customComponents: FormFields,
    schema: createSchema,
    onSubmit: submitCreateForm,
    ref: createFormRef,
    initialValues: store.uiState[storeName].get(),
    validationSchema: validationSchema(formatMessage, getApiUrl, get),
  };

  const breadCrumb = [
    {
      name: formatMessage({
        id: 'DASHBOARD',
        defaultMessage: 'Dashboard',
      }),
      icon: IconLibrary.Dashboard,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/dashboard');
      },
    },
    {
      name: formatMessage({
        id: 'SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
        defaultMessage: 'Service Administrator Warranty Types',
      }),
      icon: IconLibrary.ServiceAdministratorWarrantyTypes,
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/serviceadministratorwarrantytypes');
      },
    },
    {
      name: formatMessage({
        id: 'CREATE_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
        defaultMessage: 'Create Service Administrator Warranty Types',
      }),
      icon: IconLibrary.ServiceAdministratorWarrantyType,
      onClick: undefined,
    },
  ];

  const stackablePageButtons = [
    {
      label: formatMessage({
        id: 'LIST_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
        defaultMessage: 'List Service Administrator Warranty Types',
      }),
      Icon: IconLibrary.ServiceAdministratorWarrantyTypes,
      variant: 'outlined',
      onClick: () => {
        store.uiState[storeName].set({});
        navigate('/serviceadministratorwarrantytypes');
      },
    },
  ];

  return (
    <Layout>
      <Grid2 xs={12} container>
        <Grid2 xs={12} md={6}>
          <Breadcrumb items={breadCrumb} />
        </Grid2>
        <Grid2 xs={12} md={6}>
          <StackablePageButtons buttons={stackablePageButtons} />
        </Grid2>
      </Grid2>
      <PageSection
        label={formatMessage({
          id: 'CREATE_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
          defaultMessage: 'Create Service Administrator Warranty Types',
        })}>
        <Box>
          <CRUDComponent
            errors={formErrors}
            type={CRUD_COMPONENT_TYPE.FORM}
            formProps={formProps}>
            <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                startIcon={<IconLibrary.Save />}
                color="primary"
                onClick={() => createFormRef.current?.submit()}>
                {formatMessage({
                  id: 'SAVE_SERVICE_ADMINISTRATOR_WARRANTY_TYPES',
                  defaultMessage: 'Save Service Administrator Warranty Types',
                })}
              </PermissionButton>
              <PermissionButton
                permissionName="CAN_CREATE_EDIT_MAINTENANCE_PAGES"
                variant="contained"
                color="primary"
                startIcon={<IconLibrary.Reset />}
                onClick={() => createFormRef.current?.reset()}>
                {formatMessage({
                  id: 'RESET',
                  defaultMessage: 'Reset',
                })}
              </PermissionButton>
            </Box>
          </CRUDComponent>
        </Box>
      </PageSection>
    </Layout>
  );
}

export default observer(ServiceAdministratorWarrantyTypeCreate);
